<template>
  <div class="card-scroller-container" @click="updateActive(true)">
    <div ref="cardscroller" class="card-scroller dragscroll">
      <div ref="cardscrollercontents" class="card-scroller__contents">
        <div
          :class="
            alignBottom
              ? 'card-scroller__items align-bottom'
              : 'card-scroller__items'
          "
        >
          <slot></slot>
        </div>
      </div>
    </div>

    <div class="card-scroller__buttons" v-if="showScrollButtons">
      <button
        class="pointer"
        :class="{ disabled: startOfScroll }"
        @click="goLeft()"
      >
        <i class="mdi mdi-chevron-left"></i>
      </button>
      <button
        class="pointer"
        :class="{ disabled: endOfScroll }"
        @click="goRight()"
      >
        <i class="mdi mdi-chevron-right"></i>
      </button>
    </div>
    <template v-if="!isTouchDevice && scrollerSmallerThanContent">
      <div
        class="item-fader item-fader--right"
        :class="{ hidden: endOfScroll }"
      ></div>
      <div
        class="item-fader item-fader--left"
        :class="{ hidden: startOfScroll }"
      ></div>
    </template>
  </div>
</template>

<script>
import { PHONE_WIDTH } from '@common/constants';
import { isTouchDevice } from '@common/Helpers/mobile';
export default {
  data() {
    return {
      startOfScroll: true,
      endOfScroll: false,
      scrollerSmallerThanContent: false,
      showScrollButtons: false,
      phoneSized: false,
      isTouchDevice: false,
      mouseDownPosition: null,
      scrollerTravelling: false,
      scrollerTravelDirection: '',
      scrollerTravelDistance: 355,
      ticking: false,
      active: true,
      observer: null
    };
  },
  props: {
    alignBottom: {
      default: false
    }
  },
  methods: {
    updateActive(value) {
      // Avoids having to touch twice in order for click to go through on touch device,
      // we just make sure slot items are always active in that case.
      if (this.isTouchDevice) {
        return;
      }
      if (this.active == value) {
        return;
      }
      if (!this.$slots.default()) {
        return;
      }

      this.active = value;
    },
    determineOverflow(content, container) {
      const containerMetrics = container.getBoundingClientRect();
      const containerMetricsRight = Math.floor(containerMetrics.right);
      const containerMetricsLeft = Math.floor(containerMetrics.left);
      const contentMetrics = content.getBoundingClientRect();
      const contentMetricsRight = Math.floor(contentMetrics.right);
      const contentMetricsLeft = Math.floor(contentMetrics.left);
      if (
        containerMetricsLeft > contentMetricsLeft &&
        containerMetricsRight < contentMetricsRight
      ) {
        this.startOfScroll = false;
        this.endOfScroll = false;
        return 'both';
      } else if (contentMetricsLeft < containerMetricsLeft) {
        this.startOfScroll = false;
        this.endOfScroll = true;
        return 'left';
      } else if (contentMetricsRight > containerMetricsRight) {
        this.startOfScroll = true;
        this.endOfScroll = false;
        return 'right';
      } else {
        this.startOfScroll = true;
        this.endOfScroll = true;
        return 'none';
      }
    },
    setOverflow() {
      this.$refs.cardscroller['data-overflowing'] = this.determineOverflow(
        this.$refs.cardscrollercontents,
        this.$refs.cardscroller
      );
    },
    onTransitionEnd() {
      // get the value of the transform, apply that to the current scroll position (so get the scroll pos first) and then remove the transform
      const styleOfTransform = window.getComputedStyle(
        this.$refs.cardscrollercontents,
        null
      );
      const tr =
        styleOfTransform.getPropertyValue('-webkit-transform') ||
        styleOfTransform.getPropertyValue('transform');
      // If there is no transition we want to default to 0 and not null
      const amount = Math.abs(parseInt(tr.split(',')[4]) || 0);
      this.$refs.cardscrollercontents.style.transform = 'none';
      this.$refs.cardscrollercontents.classList.add(
        'card-scroller__contents--no-transition'
      );
      // Now lets set the scroll position
      if (this.scrollerTravelDirection === 'left') {
        this.$refs.cardscroller.scrollLeft =
          this.$refs.cardscroller.scrollLeft - amount;
      } else {
        this.$refs.cardscroller.scrollLeft =
          this.$refs.cardscroller.scrollLeft + amount;
      }
      this.scrollerTravelling = false;
    },
    goLeft() {
      // If in the middle of a move return
      if (this.scrollerTravelling === true) {
        return;
      }
      // If we have content overflowing both sides or on the left
      if (
        this.determineOverflow(
          this.$refs.cardscrollercontents,
          this.$refs.cardscroller
        ) === 'left' ||
        this.determineOverflow(
          this.$refs.cardscrollercontents,
          this.$refs.cardscroller
        ) === 'both'
      ) {
        // Find how far this panel has been scrolled
        const availableScrollLeft = this.$refs.cardscroller.scrollLeft;
        // If the space available is less than two lots of our desired distance, just move the whole amount
        // otherwise, move by the amount in the settings
        if (availableScrollLeft < this.scrollerTravelDistance * 2) {
          this.$refs.cardscrollercontents.style.transform =
            'translateX(' + availableScrollLeft + 'px)';
        } else {
          this.$refs.cardscrollercontents.style.transform =
            'translateX(' + this.scrollerTravelDistance + 'px)';
        }
        // We do want a transition (this is set in CSS) when moving so remove the class that would prevent that
        this.$refs.cardscrollercontents.classList.remove(
          'card-scroller__contents--no-transition'
        );
        // Update our settings
        this.scrollerTravelDirection = 'left';
        this.scrollerTravelling = true;
      }
      // Now update the attribute in the DOM
      this.setOverflow();
    },
    goRight() {
      // If in the middle of a move return
      if (this.scrollerTravelling === true) {
        return;
      }
      // If we have content overflowing both sides or on the right
      if (
        this.determineOverflow(
          this.$refs.cardscrollercontents,
          this.$refs.cardscroller
        ) === 'right' ||
        this.determineOverflow(
          this.$refs.cardscrollercontents,
          this.$refs.cardscroller
        ) === 'both'
      ) {
        // Get the right edge of the container and content
        const scrollerContentsRightEdge =
          this.$refs.cardscrollercontents.getBoundingClientRect().right;
        const scrollerRightEdge =
          this.$refs.cardscroller.getBoundingClientRect().right;
        // Now we know how much space we have available to scroll
        const availableScrollRight = Math.floor(
          scrollerContentsRightEdge - scrollerRightEdge
        );
        // If the space available is less than two lots of our desired distance, just move the whole amount
        // otherwise, move by the amount in the settings
        if (availableScrollRight < this.scrollerTravelDistance * 2) {
          this.$refs.cardscrollercontents.style.transform =
            'translateX(-' + availableScrollRight + 'px)';
        } else {
          this.$refs.cardscrollercontents.style.transform =
            'translateX(-' + this.scrollerTravelDistance + 'px)';
        }
        // We do want a transition (this is set in CSS) when moving so remove the class that would prevent that
        this.$refs.cardscrollercontents.classList.remove(
          'card-scroller__contents--no-transition'
        );
        // Update our settings
        this.scrollerTravelDirection = 'right';
        this.scrollerTravelling = true;
      }
      // Now update the attribute in the DOM
      this.setOverflow();
    },
    onScroll() {
      this.updateActive(false);
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.setOverflow();
          this.ticking = false;
        });
      }
      this.ticking = true;
    },
    onWindowResize() {
      this.scrollerSmallerThanContent = this.$refs.cardscroller
        ? this.$refs.cardscroller.clientWidth <
          this.$refs.cardscroller.scrollWidth
        : false;

      this.phoneSized = window.innerWidth <= PHONE_WIDTH;

      this.showScrollButtons =
        !this.phoneSized && this.scrollerSmallerThanContent;
    },
    setupDragscroll() {
      /**
       * @fileoverview dragscroll - scroll area by dragging
       * @version 0.0.8
       *
       * @license MIT, see https://github.com/asvd/dragscroll
       * @copyright 2015 asvd <heliosframework@gmail.com>
       */
      const self = this;

      (function (root, factory) {
        if (typeof define === 'function' && define.amd) {
          define(['exports'], factory);
        } else if (typeof exports !== 'undefined') {
          factory(exports);
        } else {
          factory((root.dragscroll = {}));
        }
      })(this, function (exports) {
        const _window = window;
        const _document = document;
        const mousemove = 'mousemove';
        const mouseup = 'mouseup';
        const mousedown = 'mousedown';
        const EventListener = 'EventListener';
        const addEventListener = 'add' + EventListener;
        const removeEventListener = 'remove' + EventListener;
        let newScrollX, newScrollY;

        let dragged = [];
        const reset = function (i, el) {
          for (i = 0; i < dragged.length; ) {
            el = dragged[i++];
            el = el.container || el;
            el[removeEventListener](mousedown, el.md, 0);
            _window[removeEventListener](mouseup, el.mu, 0);
            _window[removeEventListener](mousemove, el.mm, 0);
          }

          // cloning into array since HTMLCollection is updated dynamically
          dragged = [self.$refs.cardscroller];

          for (i = 0; i < dragged.length; ) {
            (function (el, lastClientX, lastClientY, pushed, scroller, cont) {
              (cont = el.container || el)[addEventListener](
                mousedown,
                (cont.md = function (e) {
                  if (
                    !el.hasAttribute('nochilddrag') ||
                    _document.elementFromPoint(e.pageX, e.pageY) == cont
                  ) {
                    pushed = 1;
                    lastClientX = e.clientX;
                    lastClientY = e.clientY;

                    e.preventDefault();
                  }
                }),
                0
              );

              _window[addEventListener](
                mouseup,
                (cont.mu = function () {
                  pushed = 0;
                }),
                0
              );

              _window[addEventListener](
                mousemove,
                (cont.mm = function (e) {
                  if (pushed) {
                    (scroller = el.scroller || el).scrollLeft -= newScrollX =
                      -lastClientX + (lastClientX = e.clientX);
                    scroller.scrollTop -= newScrollY =
                      -lastClientY + (lastClientY = e.clientY);
                    if (el == _document.body) {
                      (scroller = _document.documentElement).scrollLeft -=
                        newScrollX;
                      scroller.scrollTop -= newScrollY;
                    }
                  }
                }),
                0
              );
            })(dragged[i++]);
          }
        };

        if (_document.readyState == 'complete') {
          reset();
        } else {
          _window[addEventListener]('load', reset, 0);
        }

        exports.reset = reset;
      });
    }
  },
  mounted() {
    this.setupDragscroll();
    this.isTouchDevice = isTouchDevice();

    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');

    window.addEventListener('resize', this.onWindowResize);
    this.$refs.cardscroller.addEventListener('scroll', this.onScroll);
    this.$refs.cardscrollercontents.addEventListener(
      'transitionend',
      this.onTransitionEnd
    );

    this.observer = new MutationObserver(mutations => {
      this.setOverflow();
      this.onWindowResize();
    });

    this.observer.observe(document.querySelector('.card-scroller__items'), {
      childList: true,
      subtree: true
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    this.$refs.cardscroller.removeEventListener('scroll', this.onScroll);
    this.$refs.cardscrollercontents.removeEventListener(
      'transitionend',
      this.onTransitionEnd
    );
    this.observer.disconnect();
  },
  computed: {}
};

//t = current time
//b = start value
//c = change in value
//d = duration
Math.easeInOutQuad = function (t, b, c, d) {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t + b;
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
};
</script>

<style lang="scss" scoped>
@import '@sass/_variables.scss';

.card-scroller-container {
  overflow: visible;
  max-width: 100%;
  position: relative;
  @media only screen and (max-width: $breakpoint-phone) {
    font-size: 20px;
    .card {
      // transform: scale(0.8);
    }
  }
}

.card-scroller {
  overflow-x: hidden;

  /* We don't want vertical scrolling */
  overflow-y: hidden;
  /* For WebKit implementations, provide inertia scrolling */
  -webkit-overflow-scrolling: touch;
  /* We don't want internal inline elements to wrap */
  white-space: nowrap;
  /* If JS present, let's hide the default scrollbar */

  /* positioning context for advancers */
  position: relative;
  // Crush the whitespace here
  font-size: 0;

  /* Make this scrollable when needed */
  @media only screen and (max-width: $breakpoint-phone) {
    overflow-x: auto;
  }

  .js & {
    /* Make an auto-hiding scroller for the 3 people using a IE */
    -ms-overflow-style: -ms-autohiding-scrollbar;
    /* Remove the default scrollbar for WebKit implementations */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__contents {
    float: left;
    transition: transform 0.2s ease-in-out;
    position: relative;

    &--no-transition {
      transition: none;
    }
  }

  &__items {
    display: flex;
    max-width: 100%;
    overflow: hidden;
    padding: 0 20px 20px 20px;
    margin-bottom: 20px;
    position: relative;
  }

  .align-bottom {
    align-items: flex-end;
  }

  &__buttons {
    position: absolute;
    top: -50px;
    right: 20px;

    button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background-color: transparent;
      border: solid 0.9px rgba(0, 0, 0, 0.45);
      &:first-of-type {
        margin-right: 10px;
      }
      i {
        font-size: 20px;
      }
      &.disabled {
        border-color: #ccc;
        color: #ccc;
      }
    }
  }
}

.item-fader {
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  z-index: 99;
  width: 240px;
  position: absolute;

  &--right {
    top: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to left,
      rgba(251, 251, 251, 1),
      rgba(251, 251, 251, 0)
    );
  }
  &--left {
    top: 0;
    left: 0;
    bottom: 0;
    background: linear-gradient(
      to right,
      rgba(251, 251, 251, 1),
      rgba(251, 251, 251, 0)
    );
  }
  &.hidden {
    display: block;
    opacity: 0;
  }
}
</style>

<style lang="scss">
@import '@sass/_variables.scss';
@media only screen and (max-width: $breakpoint-phone) {
  .card-scroller__items .card {
    max-width: 85vw;
    min-width: 85vw;
    .card__image {
      max-height: 130px;
    }
    .card__bottom {
      .bottom-content {
        overflow: auto;
      }
    }
  }
}

.card-scroller__items .card:not(:last-of-type) {
  margin-right: 20px;
}
* {
  box-sizing: inherit;
}
</style>
